import React from 'react';
import { Grid, Image } from 'antd';
import logo from 'assets/images/farmers-plan-logo.svg';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'types';
import { LayoutIdentityHeader } from 'components/LayoutIdentityHeader';
import { SectionNavigation, SECTION_NAVIGATION_PATHS } from 'components/SectionNavigation';

const STEPS = [
  { path: '/register', key: 'OnboardingStep1' },
  { path: '/validate', key: 'OnboardingStep2' },
];

const FULL_WIDTH_PATHS = ['/farm-details', '/users'];
const IDENTITY_HEADER_PATHS = ['/farm-details', '/users'];

export function OnboardingLayout({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const { t }: TFunction = useTranslation();
  const screens = Grid.useBreakpoint();
  const currentStep = STEPS.findIndex(s => s.path === location.pathname);
  const isFullWidthPage = FULL_WIDTH_PATHS.includes(location.pathname);
  const showSectionNavigation = SECTION_NAVIGATION_PATHS.includes(location.pathname);
  const showIdentityHeader = IDENTITY_HEADER_PATHS.includes(location.pathname);
  const showStandaloneSectionNavigation = showSectionNavigation && !showIdentityHeader;
  const fullWidthSidePadding = !screens.md ? 16 : 24;

  return (
    <div style={{
      minHeight: '100vh',
      background: '#F9F9F9',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      overflowX: 'hidden',
    }}>
      <div
        style={{
          paddingTop: showIdentityHeader ? 32 : showSectionNavigation ? 24 : 48,
          paddingBottom: showSectionNavigation ? 24 : 32,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          width: '100%',
        }}
      >
        {showIdentityHeader ? (
          <LayoutIdentityHeader maxWidth={1440} showMenu={showSectionNavigation} />
        ) : (
          <Image src={logo} preview={false} style={{ width: 160 }} />
        )}
        {showStandaloneSectionNavigation ? <SectionNavigation /> : null}
      </div>

      {currentStep >= 0 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 40 }}>
          {STEPS.map((step, index) => {
            const isDone = index < currentStep;
            const isCurrent = index === currentStep;
            return (
              <React.Fragment key={step.path}>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
                  <div style={{
                    width: 28,
                    height: 28,
                    borderRadius: '50%',
                    backgroundColor: isDone ? '#52c41a' : isCurrent ? '#ff6b3d' : '#e0e0e0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#fff',
                    fontSize: 12,
                    fontWeight: 600,
                    transition: 'background-color 0.3s',
                  }}>
                    {isDone ? '✓' : index + 1}
                  </div>
                  <span style={{
                    fontSize: 11,
                    color: isCurrent ? '#333' : '#bbb',
                    fontWeight: isCurrent ? 600 : 400,
                    whiteSpace: 'nowrap',
                  }}>
                    {t(step.key)}
                  </span>
                </div>
                {index < STEPS.length - 1 && (
                  <div style={{
                    width: 52,
                    height: 1,
                    backgroundColor: index < currentStep ? '#52c41a' : '#e0e0e0',
                    marginBottom: 20,
                    transition: 'background-color 0.3s',
                  }} />
                )}
              </React.Fragment>
            );
          })}
        </div>
      )}

      <div
        style={{
          width: '100%',
          maxWidth: isFullWidthPage ? '100%' : 368,
          padding: isFullWidthPage ? `0 ${fullWidthSidePadding}px` : '0 24px',
          flex: 1,
          boxSizing: 'border-box',
        }}
      >
        {children}
      </div>

      <div style={{ padding: '32px 0', textAlign: 'center' }}>
        <a
          href="https://www.farmersplan.com/villkor"
          target="_blank"
          rel="noreferrer"
          style={{ color: '#ccc', fontSize: 12, textDecoration: 'none' }}
        >
          {t('TermsAndConditions')}
        </a>
      </div>
    </div>
  );
}
